import React from 'react'

import {
  TextInputField,
  GridWrapper,
  Accordion,
  AccordionItem,
} from '@sainsburys-tech/luna-react'

import CodeSnippet from './CodeSnippet'

const AnnounceUpdates = () => (
  <section>
    <h2>Announce content updates and notifications to screen readers</h2>

    <p>
      When a user action triggers a change in content, like an error message or
      a notification, make sure the new content is announced to screen reader
      users.
    </p>

    <Accordion standalone>
      <AccordionItem title="Why announce content updates?">
        <p>You should announce content updates and notifications because:</p>

        <ul>
          <li>
            native HTML doesn’t announce all visual content changes to assistive
            technologies like screen readers
          </li>
          <li>
            if your users can’t see the new content, they won’t know it’s there
          </li>
        </ul>

        <p>
          There’s a number of ARIA roles, states, and properties that can be
          used to notify screen reader users about changes in content, including
          the{' '}
          <a href="https://www.w3.org/TR/wai-aria-1.1/#alert">
            <code>alert</code>
          </a>{' '}
          and{' '}
          <a href="https://www.w3.org/TR/wai-aria-1.1/#alertdialog">
            <code>alertdialog</code>
          </a>{' '}
          roles.
        </p>

        <p>
          Examples of content changes that are not announced include, but are
          not limited to:
        </p>

        <ul>
          <li>filters that update a list of search results</li>
          <li>form validation errors that require a user action</li>
        </ul>
      </AccordionItem>
    </Accordion>

    <GridWrapper equalHeight>
      <CodeSnippet
        type="positive"
        component={
          <TextInputField
            type="password"
            defaultValue="password"
            name="content-updates-1"
            label=""
            error="This error will be announced to screen reader users who can't see visual changes in content"
          />
        }
        code={`<div role="alert" id="validation">
                <p>This error...</p>
                </div>
                
                <input aria-describedby="validation" aria-invalid="true" />`}
      />
      <CodeSnippet
        type="negative"
        component={
          <div className="ln-c-form-group has-error">
            <div
              id="content-updates-2Validation"
              className="ln-c-field-info ln-c-field-info--error"
            >
              This error won&apos;t be announced to screen reader users who rely
              on visual changes in content being announced
            </div>
            <div className="ln-c-input-group">
              <input
                type="password"
                id="content-updates-2"
                name="content-updates-2"
                className="ln-c-text-input ln-c-input-group__control"
                value="password"
              />
            </div>
          </div>
        }
        code={`<div>
                <p>This error...</p>
                </div>
                
                <input />`}
      />
    </GridWrapper>
  </section>
)

export default AnnounceUpdates
